/**
 * PURPOSE: Sub-tab "Acumulada" de POSICIONES: tabla acumulada nueva entre grupos (FTR-0065).
 * RESPONSIBILITIES: Mostrar lo que calcula el backend (nunca calcula posiciones en el cliente): barra con los
 *   criterios del deporte, bandas por nivel con su cantidad de equipos, equipo con logo (iniciales si no hay) y su
 *   grupo/puesto ("C · 1.º"), columnas del deporte con los criterios resaltados, marcas como chips (Solo WO, Sin
 *   partidos, Empate, incluye N WO), avisos (Parcial, Tabla desactualizada → Recalcular, cruces ya armados) y, en los
 *   empates perfectos, subir/bajar SOLO entre los equipos empatados (el backend valida y recalcula).
 * NOTES: La tabla va dentro de un contenedor con scroll horizontal y las columnas # y Equipo quedan fijas (sticky):
 *   en pantallas chicas no desborda la página. Solo presentación: el logo viene en `team.image` del endpoint.
 * COLLABORATORS: services/accumulatedStandingsService.js, utils/accumulatedStandingsDisplay.js, PosicionesArea.jsx.
 * ROUTE: winscore/src/area/PosicionesArea/acumulada/AcumuladaStandings.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import { FaAngleDown, FaAngleUp, FaInfoCircle } from "react-icons/fa";
import { toast } from "react-hot-toast";

import {
  accumulatedErrorMessage,
  getAccumulatedStandings,
  recalculateAccumulatedStandings,
  saveAccumulatedTieOrder,
} from "../../../services/accumulatedStandingsService.js";
import {
  COLUMN_TITLES,
  accumulatedNotices,
  columnValue,
  formatAccumulatedValue,
  moveWithinTie,
  rowBadges,
  sectionsByLevel,
  teamInitials,
} from "../../../utils/accumulatedStandingsDisplay.js";

const NOTICE_TONES = {
  amber: "bg-amber-50 text-amber-900 ring-amber-200",
  red: "bg-amber-50 text-amber-900 ring-amber-200",
  slate: "bg-slate-50 text-slate-600 ring-slate-200",
};
const BADGE_TONES = {
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};
// Columnas fijas a la izquierda al desplazar en pantallas chicas: # (3.5 rem) y equipo.
const STICKY_RANK = "sticky left-0 z-10 w-14 min-w-[3.5rem] max-w-[3.5rem]";
const STICKY_TEAM = "sticky left-14 z-10 min-w-[11rem] shadow-[inset_-1px_0_0_#e5e7eb]";
const TH = "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap bg-slate-50";
const TD = "px-3 py-2.5 text-sm text-slate-800 whitespace-nowrap tabular-nums";

/** Logo del equipo en un contenedor redondeado; si no hay logo o falla la carga, sus iniciales. */
const TeamLogo = ({ team }) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(team?.image) && !failed;
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
      {showImage ? (
        <img
          src={team.image}
          alt={team.abbreviation || team.name || "Logo"}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-contain p-0.5"
        />
      ) : (
        <span className="text-[10px] font-bold text-slate-500" aria-hidden="true">
          {teamInitials(team)}
        </span>
      )}
    </span>
  );
};

const Chip = ({ tone, children }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${BADGE_TONES[tone]}`}>{children}</span>
);

const IconButton = ({ label, disabled, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="flex h-5 w-5 items-center justify-center rounded text-slate-500 hover:bg-slate-200 hover:text-blue-600 disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

const AcumuladaStandings = ({ idevent, idsport }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setView(await getAccumulatedStandings({ idevent, idsport }));
    } catch (loadError) {
      setError(accumulatedErrorMessage(loadError, "No se pudo cargar la tabla acumulada."));
    } finally {
      setLoading(false);
    }
  }, [idevent, idsport]);

  useEffect(() => {
    load();
  }, [load]);

  const recalculate = async () => {
    setBusy(true);
    try {
      const result = await recalculateAccumulatedStandings({ idevent, idsport });
      if (result?.view) setView(result.view);
      if (result?.recalculation?.accumulated?.ok === false) toast.error("No se pudo regenerar la tabla acumulada; sigue desactualizada.");
      else toast.success("¡Posiciones y tabla acumulada recalculadas!");
    } catch (recalcError) {
      toast.error(accumulatedErrorMessage(recalcError, "No se pudo recalcular la tabla acumulada."));
    } finally {
      setBusy(false);
    }
  };

  const move = async (idparticipant, direction) => {
    const order = moveWithinTie(view.rows, idparticipant, direction);
    if (!order) return;
    setBusy(true);
    try {
      const result = await saveAccumulatedTieOrder({ idevent, idsport, idparticipants: order });
      if (result?.view) setView(result.view);
      toast.success("¡Orden del empate guardado!");
    } catch (moveError) {
      toast.error(accumulatedErrorMessage(moveError, "No se pudo guardar el orden del empate."));
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading && !view) return <p className="px-4 py-6 text-sm text-gray-600">Cargando tabla acumulada…</p>;
  if (error) {
    return (
      <div className="mx-4 my-6 rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200">
        {error}{" "}
        <button type="button" onClick={load} className="font-semibold underline">
          Reintentar
        </button>
      </div>
    );
  }
  if (!view?.available) {
    const reason = view?.reason === "single_group" ? "La acumulada necesita al menos dos grupos." : "No hay tabla acumulada para este deporte.";
    return <p className="px-4 py-6 text-sm text-gray-600">{reason}</p>;
  }

  const notices = accumulatedNotices(view);
  const columns = view.columns || [];
  const sections = sectionsByLevel(view.rows);
  const firstCriterion = columns.findIndex((column) => column.criterion);

  return (
    <div className="space-y-4 px-4" data-testid="acumulada-standings">
      <div className="card overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 pb-3 pt-4">
          <h2 className="text-lg font-bold text-slate-900">Tabla acumulada</h2>
          <p className="mt-2 inline-flex max-w-full items-start gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
            <FaInfoCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" aria-hidden="true" />
            <span>{view.headline}</span>
          </p>
        </div>

        {notices.length ? (
          <ul className="space-y-2 px-5 pb-3">
            {notices.map((notice) => (
              <li key={notice.key} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs ring-1 ring-inset ${NOTICE_TONES[notice.tone]}`}>
                <span className="font-semibold">{notice.text}</span>
                {notice.action === "recalculate" ? (
                  <button
                    type="button"
                    onClick={recalculate}
                    disabled={busy}
                    className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    {busy ? "Recalculando…" : "Recalcular"}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="overflow-x-auto border-t border-slate-200" data-testid="acumulada-scroll">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th scope="col" className={`${TH} ${STICKY_RANK} text-center text-slate-500`}>
                  #
                </th>
                <th scope="col" className={`${TH} ${STICKY_TEAM} text-left text-slate-500`}>
                  Equipo
                </th>
                {columns.map((column, index) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`${TH} text-center ${column.criterion ? "bg-blue-50 text-blue-700" : "text-slate-500"} ${index === firstCriterion ? "border-l border-blue-100" : ""}`}
                  >
                    <abbr title={COLUMN_TITLES[column.key] || column.label} className="no-underline">
                      {column.label}
                    </abbr>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <React.Fragment key={section.key}>
                  <tr>
                    <th colSpan={columns.length + 2} scope="colgroup" className="border-y border-slate-200 bg-slate-100 px-4 py-2 text-left">
                      <span className="sticky left-4 inline-flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{section.label}</span>
                        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
                          {section.rows.length} {section.rows.length === 1 ? "equipo" : "equipos"}
                        </span>
                      </span>
                    </th>
                  </tr>
                  {section.rows.map((row) => {
                    const canUp = Boolean(moveWithinTie(view.rows, row.idparticipant, -1));
                    const canDown = Boolean(moveWithinTie(view.rows, row.idparticipant, 1));
                    const badges = rowBadges(row);
                    const teamName = row.team?.abbreviation || row.team?.name;
                    const rowBg = row.tie ? "bg-amber-50" : "bg-white";
                    return (
                      <tr key={row.idparticipant} data-testid={`acumulada-row-${row.idparticipant}`} className="group">
                        <td className={`${TD} ${STICKY_RANK} ${rowBg} border-b border-slate-100 group-hover:bg-slate-50`}>
                          <div className="flex items-center justify-center gap-1">
                            <span
                              className={`flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-bold ${
                                row.rank <= 3 && !row.walkoverOnly ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {row.rank}
                            </span>
                            {row.tie ? (
                              <span className="flex flex-col">
                                <IconButton label={`Subir ${teamName} dentro del empate`} disabled={busy || !canUp} onClick={() => move(row.idparticipant, -1)}>
                                  <FaAngleUp />
                                </IconButton>
                                <IconButton label={`Bajar ${teamName} dentro del empate`} disabled={busy || !canDown} onClick={() => move(row.idparticipant, 1)}>
                                  <FaAngleDown />
                                </IconButton>
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className={`${TD} ${STICKY_TEAM} ${rowBg} border-b border-slate-100 group-hover:bg-slate-50`}>
                          <div className="flex items-center gap-3">
                            <TeamLogo team={row.team} />
                            <div className="min-w-0">
                              <div className="truncate font-bold text-slate-900" title={row.team?.name || ""}>
                                {row.team?.abbreviation || row.team?.name}
                              </div>
                              <div className="text-xs font-medium text-slate-400">{row.groupTag}</div>
                              {badges.length ? (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {badges.map((badge) => (
                                    <Chip key={badge.key} tone={badge.tone}>
                                      {badge.text}
                                    </Chip>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        {columns.map((column, index) => (
                          <td
                            key={column.key}
                            className={`${TD} border-b border-slate-100 text-center ${
                              column.criterion
                                ? `bg-blue-50/50 font-bold text-slate-900 group-hover:bg-blue-50 ${index === firstCriterion ? "border-l border-l-blue-100 text-blue-800" : ""}`
                                : "group-hover:bg-slate-50"
                            }`}
                          >
                            {formatAccumulatedValue(columnValue(row, column.key))}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
          Los equipos que solo perdieron por W.O. van al final de la tabla. En un empate perfecto, las flechas cambian el orden
          solo entre los equipos empatados.
        </p>
      </div>
    </div>
  );
};

export default AcumuladaStandings;
